import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  FaMousePointer,
  FaClock,
  FaExclamationCircle,
  FaCalendarAlt,
  FaEye,
} from 'react-icons/fa';
import { fetchHeatmapAnalytics } from '../services/heatmapService';



/** Auto-refresh interval in milliseconds */
const REFRESH_INTERVAL_MS = 60_000;

/** Canvas render dimensions (logical viewport the click  are mapped from) */
const VIEWPORT = { width: 1024, height: 768 };

/** Canvas height in pixels */
const CANVAS_HEIGHT = 600;

/** Platform base URL for iframe preview */
const PLATFORM_BASE_URL = process.env.REACT_APP_PLATFORM_URL || 'https://devserver.spatialgrid.ai';

/**
 * Color stops used to render the heatmap gradient from cold → hot.
 * Each value corresponds to an evenly-spaced intensity bucket.
 */
const HEATMAP_COLORS = [
  'rgba(0,   0,   255, 0.5)', // cold  – blue
  'rgba(0,   255, 255, 0.5)', // cyan
  'rgba(0,   255, 0,   0.5)', // green
  'rgba(255, 255, 0,   0.5)', // yellow
  'rgba(255, 0,   0,   0.6)', // hot   – red
];

/** Default stats shown before data loads */
const DEFAULT_STATS = {
  totalClicks: 0,
  totalPages: 0,
  activeDevices: 0,
  avgClicksPerPage: 0,
};

// ─── Inline CSS ────────────────────────────────────────────────────────────────

/**
 * Thin, themed scrollbar styles injected once via <style>.
 * Scoped to `.custom-scrollbar` to avoid polluting global styles.
 */
const SCROLLBAR_CSS = `
  .custom-scrollbar { overflow-y: auto; scrollbar-width: thin; scrollbar-color: #4B5563 #2D2F33; }
  .custom-scrollbar::-webkit-scrollbar { width: 8px; }
  .custom-scrollbar::-webkit-scrollbar-track { background: #2D2F33; border-radius: 10px; }
  .custom-scrollbar::-webkit-scrollbar-thumb { background: #4B5563; border-radius: 10px; }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #5A6374; }
`;

// ─── Pure Helpers ──────────────────────────────────────────────────────────────

/**
 * Derives summary stats from the raw click records array.
 *
 * @param {Array} records - Raw click data from the API
 * @returns {Object} { totalClicks, totalPages, activeDevices, avgClicksPerPage }
 */
const buildStatsData = (records) => {
  const totalClicks  = records.length;
  const uniquePages  = new Set(records.map((c) => c.path)).size;
  const uniqueDevices = new Set(records.map((c) => c.deviceId)).size;

  return {
    totalClicks,
    totalPages: uniquePages,
    activeDevices: uniqueDevices,
    avgClicksPerPage: uniquePages > 0 ? Math.round(totalClicks / uniquePages) : 0,
  };
};

/**
 * Maps an intensity value (0–1) to an RGB color from the heatmap gradient.
 *
 * @param {number} intensity - Normalized intensity (0–1)
 * @returns {Object} { r, g, b } color values (0–255)
 */
/**
 * Parse RGBA strings to color components
 * @param {string} str - RGBA string like "rgba(r, g, b, a)"
 * @returns {Array} [r, g, b, a]
 */
const parseRgba = (str) => {
  const match = str.match(/\d+/g);
  return match ? match.map(Number) : [0, 0, 0, 0];
};

/**
 * Maps intensity (0–1) to RGB color using linear interpolation
 * @param {number} intensity - Normalized intensity (0–1)
 * @returns {Object} { r, g, b } color values (0–255)
 */
const getHeatColor = (intensity) => {
  const t = Math.max(0, Math.min(1, intensity));
  const index = t * (HEATMAP_COLORS.length - 1);
  const lowerIdx = Math.floor(index);
  const upperIdx = Math.ceil(index);
  const localT = index - lowerIdx;

  const [r1, g1, b1] = parseRgba(HEATMAP_COLORS[lowerIdx]);
  const [r2, g2, b2] = parseRgba(HEATMAP_COLORS[upperIdx]);

  return {
    r: Math.round(r1 + (r2 - r1) * localT),
    g: Math.round(g1 + (g2 - g1) * localT),
    b: Math.round(b1 + (b2 - b1) * localT),
  };
};

/**
 * Draws radial gradient blobs for click density visualization
 * @param {CanvasRenderingContext2D} ctx - Canvas rendering context
 * @param {Array} data - Click records for current page
 * @param {number} width - Canvas width (px)
 * @param {number} height - Canvas height (px)
 */
const drawHeatmap = (ctx, data, width, height) => {
  if (!data.length) return;

  const off = document.createElement('canvas');
  off.width = width;
  off.height = height;
  const offCtx = off.getContext('2d');

  const max = Math.max(...data.map(d => d.count || 1), 1);

  data.forEach(point => {
    const x = point.clickPosition?.x ?? point.x ?? 0;
    const y = point.clickPosition?.y ?? point.y ?? 0;

    const cx = (x / VIEWPORT.width) * width;
    const cy = (y / VIEWPORT.height) * height;

    const intensity = (point.count || 1) / max;
    const radius = 25 + intensity * 50;

    const g = offCtx.createRadialGradient(cx, cy, 0, cx, cy, radius);
    g.addColorStop(0, `rgba(0,0,0,${0.6 * intensity})`);
    g.addColorStop(1, 'rgba(0,0,0,0)');

    offCtx.fillStyle = g;
    offCtx.beginPath();
    offCtx.arc(cx, cy, radius, 0, Math.PI * 2);
    offCtx.fill();
  });

  const img = offCtx.getImageData(0, 0, width, height);
  const pixels = img.data;

  for (let i = 0; i < pixels.length; i += 4) {
    const alpha = pixels[i + 3];
    if (alpha === 0) continue;

    const color = getHeatColor(alpha / 255);
    pixels[i] = color.r;
    pixels[i + 1] = color.g;
    pixels[i + 2] = color.b;
  }

  ctx.putImageData(img, 0, 0);
};

// ─── Sub-components ────────────────────────────────────────────────────────────

/**
 * Summary metric card with icon
 */
const StatCard = ({ title, value, icon: Icon }) => (
  <div className="bg-[#1D1E22] rounded-lg p-3 sm:p-4 flex flex-col border border-gray-800 hover:border-gray-600 transition">
    <span className="text-gray-400 text-xs sm:text-sm mb-1">{title}</span>
    <div className="flex justify-between items-center">
      <span className="text-white text-2xl sm:text-3xl md:text-4xl font-bold">{value}</span>
      <div className="p-1 sm:p-2 rounded-full min-w-[32px] min-h-[32px] flex items-center justify-center shrink-0 bg-gray-700">
        <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
      </div>
    </div>
  </div>
);

/**
 * Loading indicator banner
 */
const LoadingBanner = () => (
  <div className="mt-4 p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg flex items-center gap-3">
    <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
    <span className="text-blue-300">Loading heatmap analytics...</span>
  </div>
);

/**
 * Error banner with retry button
 */
const ErrorBanner = ({ message }) => (
  <div className="mt-4 p-4 bg-red-900/20 border border-red-700/50 rounded-lg flex items-start gap-3">
    <FaExclamationCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
    <div className="flex-1">
      <p className="text-red-300 font-semibold">Error Loading Analytics</p>
      <p className="text-red-200 text-sm mt-1">{message}</p>
    </div>
    <button
      onClick={() => window.location.reload()}
      className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold whitespace-nowrap flex-shrink-0"
    >
      Retry
    </button>
  </div>
);

/**
 * Page navigation controls
 */
const PageNavigator = ({ current, total, pagePath, onPrev, onNext }) => (
  <div className="mt-4 px-2 sm:px-4 flex flex-col sm:flex-row items-center gap-3 bg-[#1D1E22] rounded-lg p-3 sm:p-4 border border-[#2D2F33]">
    <button
      onClick={onPrev}
      disabled={current === 0}
      className={`px-4 py-2 rounded-lg font-semibold transition ${
        current === 0
          ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
          : 'bg-blue-600 text-white hover:bg-blue-700'
      }`}
    >
      ← Previous
    </button>

    <div className="px-4 py-2 bg-[#2D2F33] rounded-lg font-semibold text-white min-w-fit">
      Page {current + 1} of {total}
    </div>

    <button
      onClick={onNext}
      disabled={current === total - 1}
      className={`px-4 py-2 rounded-lg font-semibold transition ${
        current === total - 1
          ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
          : 'bg-blue-600 text-white hover:bg-blue-700'
      }`}
    >
      Next →
    </button>

    <div className="ml-auto text-sm text-gray-400">
      {pagePath ? `Current: ${pagePath}` : 'All Pages'}
    </div>
  </div>
);

/**
 * Page selector sidebar
 */
const PageSelector = ({ pages, selectedPage, onSelect }) => (
  <div className="bg-[#1D1E22] rounded-lg p-3 sm:p-4 border border-[#2D2F33]">
    <h3 className="font-medium text-white mb-3 sm:mb-4">Tracked Pages</h3>
    <div className="flex flex-col gap-2 max-h-80 custom-scrollbar">
      {pages.length > 0 ? (
        <>
          <button
            onClick={() => onSelect(null)}
            className={`px-3 py-2 rounded text-sm text-left transition ${
              selectedPage === null
                ? 'bg-blue-600 text-white'
                : 'bg-[#2D2F33] text-gray-100 hover:bg-[#3D3F43]'
            }`}
          >
            All Pages
          </button>
          {pages.map((page) => (
            <button
              key={page}
              title={page}
              onClick={() => onSelect(page)}
              className={`px-3 py-2 rounded text-sm text-left break-words transition ${
                selectedPage === page
                  ? 'bg-blue-600 text-white'
                  : 'bg-[#2D2F33] text-gray-100 hover:bg-[#3D3F43]'
              }`}
            >
              {page}
            </button>
          ))}
        </>
      ) : (
        <span className="text-gray-400 text-sm">No pages tracked yet</span>
      )}
    </div>
  </div>
);

/**
 * Page-specific statistics panel
 */
const PageStatsPanel = ({ data }) => {
  const uniquePositions = new Set(data.map((d) => `${d.x},${d.y}`)).size;
  const uniqueDevices = new Set(data.map((d) => d.deviceId)).size;

  const rows = [
    { label: 'Total Clicks', value: data.length },
    { label: 'Unique Positions', value: uniquePositions },
    { label: 'Unique Devices', value: uniqueDevices },
  ];

  return (
    <div className="bg-[#1D1E22] rounded-lg p-3 sm:p-4 border border-[#2D2F33]">
      <h3 className="font-medium text-white mb-3">Page Statistics</h3>
      <div className="space-y-2 text-sm">
        {rows.map(({ label, value }) => (
          <div key={label} className="flex justify-between">
            <span className="text-gray-400">{label}:</span>
            <span className="text-white font-medium">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Canvas component for iframe + heatmap overlay
 */
const HeatmapCanvas = ({ data, pagePath }) => {
  const canvasRef = useRef(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  const pageUrl = pagePath ? `${PLATFORM_BASE_URL}${pagePath}` : PLATFORM_BASE_URL;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !data?.length) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.offsetWidth;

    canvas.width = width;
    canvas.height = CANVAS_HEIGHT;

    ctx.clearRect(0, 0, width, CANVAS_HEIGHT);
    drawHeatmap(ctx, data, width, CANVAS_HEIGHT);
  }, [data, pagePath]);

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: `${CANVAS_HEIGHT}px`,
        borderRadius: 4,
        border: '1px solid #3D3F43',
        background: '#1D1E22',
        overflow: 'hidden',
      }}
    >
      <iframe
        src={pageUrl}
        title="Page Preview"
        scrolling="yes"
        onLoad={() => setIframeLoaded(true)}
        onError={() => setIframeLoaded(false)}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          border: 'none',
          background: '#ffffff',
          zIndex: 1,
          pointerEvents: 'none',
        }}
      />

      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'transparent',
          zIndex: 2,
          pointerEvents: 'none',
        }}
      />

      <div
        style={{
          position: 'absolute',
          top: 8,
          left: 8,
          background: 'rgba(0,0,0,0.7)',
          color: '#00FF00',
          padding: '4px 8px',
          borderRadius: 4,
          fontSize: 11,
          fontFamily: 'monospace',
          zIndex: 3,
          pointerEvents: 'none',
        }}
      >
        📄 {pagePath || '/'} {!iframeLoaded && '(Loading...)'}
      </div>

      {!iframeLoaded && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'rgba(0,0,0,0.8)',
            color: '#888',
            padding: 16,
            borderRadius: 4,
            textAlign: 'center',
            fontSize: 12,
            fontFamily: 'monospace',
            zIndex: 4,
            pointerEvents: 'none',
          }}
        >
          <div style={{ color: '#ffff00', marginBottom: 8 }}>⏳ Loading page...</div>
          <div style={{ fontSize: 10, color: '#666' }}>{pageUrl}</div>
        </div>
      )}
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────

/**
 * HeatmapsAnalysis - Click heatmap analytics dashboard
 * Displays click density overlays and engagement metrics for tracked pages
 */
const HeatmapsAnalysis = ({ projectTitle = 'All Experiences' }) => {
  const [heatmapData, setHeatmapData] = useState(null);
  const [selectedPage, setSelectedPage] = useState(null);
  const [statsData, setStatsData] = useState(DEFAULT_STATS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchHeatmapAnalytics();
      console.log('Heatmap Analytics:', data);
      setHeatmapData(data);

      if (Array.isArray(data?.data)) {
        setStatsData(buildStatsData(data.data));
      }
    } catch (err) {
      console.error('Heatmap fetch error:', err);
      setError(err.message ?? 'Failed to fetch heatmap data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const intervalId = setInterval(loadData, REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [loadData]);

  useEffect(() => {
    if (!selectedPage && heatmapData?.data?.length) {
      const first = [...new Set(heatmapData.data.map((c) => c.path))][0];
      setSelectedPage(first);
    }
  }, [heatmapData, selectedPage]);

  const uniquePages = useMemo(
    () => (heatmapData?.data ? [...new Set(heatmapData.data.map((c) => c.path))] : []),
    [heatmapData]
  );

  const filteredData = useMemo(
    () =>
      selectedPage
        ? (heatmapData?.data?.filter((c) => c.path === selectedPage) ?? [])
        : [],
    [heatmapData, selectedPage]
  );

  const currentPageIndex = useMemo(
    () => (selectedPage ? uniquePages.indexOf(selectedPage) : 0),
    [uniquePages, selectedPage]
  );

  const handlePrevPage = useCallback(() => {
    if (currentPageIndex > 0) setSelectedPage(uniquePages[currentPageIndex - 1]);
  }, [currentPageIndex, uniquePages]);

  const handleNextPage = useCallback(() => {
    if (currentPageIndex < uniquePages.length - 1) setSelectedPage(uniquePages[currentPageIndex + 1]);
  }, [currentPageIndex, uniquePages]);

  return (
    <>
      <style>{SCROLLBAR_CSS}</style>

      <div className="flex flex-col bg-[#18191C] min-h-screen overflow-auto p-3 sm:p-4 md:p-6 w-full">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-2">
          <h1 className="text-xl sm:text-2xl md:text-[28px] font-bold text-white">
            Heatmaps and Spatial Analytics
          </h1>
          <div className="bg-[#2D2F33] rounded-lg px-3 py-2 sm:px-4 flex items-center gap-2 w-full sm:w-auto shrink-0">
            <span className="text-gray-300 text-sm whitespace-nowrap">Project:</span>
            <span className="font-medium text-white text-sm truncate max-w-[150px] md:max-w-[200px]">
              {projectTitle}
            </span>
          </div>
        </div>

        {/* Status Banners */}
        {error && <ErrorBanner message={error} />}
        {loading && <LoadingBanner />}

        {/* Page Navigation */}
        {uniquePages.length > 0 && (
          <PageNavigator
            current={currentPageIndex}
            total={uniquePages.length}
            pagePath={selectedPage}
            onPrev={handlePrevPage}
            onNext={handleNextPage}
          />
        )}

        <div className="p-2 sm:p-4" />

        {/* Summary Stats */}
        <div className="px-2 sm:px-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <StatCard title="Total Clicks Recorded" value={statsData.totalClicks} icon={FaMousePointer} />
          <StatCard title="Pages Tracked" value={statsData.totalPages} icon={FaEye} />
          <StatCard title="Active Devices" value={statsData.activeDevices} icon={FaExclamationCircle} />
          <StatCard title="Avg Clicks / Page" value={statsData.avgClicksPerPage} icon={FaClock} />
        </div>

        {/* Toolbar */}
        <div className="mt-4 sm:mt-6 px-2 sm:px-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-wrap">
          <span className="text-gray-300 text-xs sm:text-sm">
            Showing {selectedPage ? '1' : uniquePages.length} of {uniquePages.length} pages
          </span>
          <div className="sm:ml-auto flex items-center bg-[#2D2F33] rounded-lg px-2 py-1 sm:px-3 sm:py-2">
            <FaCalendarAlt className="w-4 h-4 text-white mr-1 sm:mr-2" />
            <span className="text-white text-xs sm:text-sm">Last 7 days</span>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex flex-col lg:flex-row mt-4 px-2 sm:px-4 gap-3 sm:gap-4">
          <div className="w-full flex-1 border border-[#2D2F33] rounded-lg p-4 bg-[#1D1E22] min-h-[400px]">
            <h3 className="text-white font-medium mb-4">Click Heatmap Visualization</h3>
            {loading ? (
              <div className="flex items-center justify-center h-80">
                <span className="text-gray-400">Loading heatmap data...</span>
              </div>
            ) : filteredData.length > 0 ? (
              <HeatmapCanvas data={filteredData} pagePath={selectedPage} />
            ) : (
              <div className="flex items-center justify-center h-80">
                <span className="text-gray-400">No click data available for selected page</span>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="w-full lg:w-80 xl:w-96 flex flex-col gap-4">
            <PageSelector pages={uniquePages} selectedPage={selectedPage} onSelect={setSelectedPage} />
            {selectedPage && filteredData.length > 0 && <PageStatsPanel data={filteredData} />}
          </div>
        </div>
      </div>
    </>
  );
};

export default HeatmapsAnalysis;